import React, { useState, useEffect } from 'react'
import { Layout, Row, Col } from 'antd'
import styled from 'styled-components'
import { saveAs } from 'file-saver'
import { translate } from 'i18n'
import TKeys from 'i18n/translationKey'

import file from 'utils/file'
import { LIMIT_FILE } from 'config'

import Picker from '../../Shared/Form/Picker'
import { TrashBtn, DownloadDisableBtn } from '../../Shared/Button'
import Editor from '../Editor'

import imgCheck from 'assets/img/ic-check.png'

type Props = {
  readOnly: boolean
  images: any[]
  numberOfImage?: number
  onClose: () => void
  onThumbnailOpen: (index: number, list?: any[], readOnly?: boolean) => void
  onChange: (list: any[]) => void
  breadcrumb?: any
  isMultiple?: boolean
  onChangeRemove?: (list: any[]) => void
}

const ImageDisplay: React.ComponentType<Props> = props => {
  const [showEditor, setShowEditor] = useState(false)
  const [fileEditor, setFileEditor] = useState({
    base64: '',
    file: { name: '' },
    lastModified: 0,
  })
  const [loading, setLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [error, setError] = useState('')

  const [edit, setEdit] = useState(false)
  const [checkList, setCheckList] = useState([] as number[])
  const [removeImgList, setRemoveImgList] = useState([] as any[])

  const {
    images,
    readOnly,
    onChange,
    breadcrumb,
    numberOfImage = 5,
    isMultiple = true,
    onChangeRemove,
  } = props

  const addFile = async (base64: any, fileItem: any) => {
    try {
      const lastModified = fileItem.lastModified
      let resp
      if (fileItem.type === 'image/heic') {
        setLoading(true)
        resp = await file.heicToJpeg({
          file: fileItem,
          filename: fileItem.name,
        })
        base64 = resp.text
        fileItem = resp.file
        setLoading(false)
      }

      if (fileItem.size < LIMIT_FILE.image) {
        resp = await file.compress({
          value: base64,
          file: fileItem,
          limit: LIMIT_FILE.image,
        })
        base64 = resp.text
        fileItem = resp.file
      }

      setShowEditor(true)
      setFileEditor({ base64, file: fileItem, lastModified })
    } catch (e) {
      setError(translate(TKeys.Error.file_wrong))
      setShowError(true)
    }

    setLoading(false)
  }

  const onAddImage = async (base64: string) => {
    try {
      const params = {
        data: base64,
        filename: fileEditor.file.name,
        lastModified: fileEditor.lastModified,
      }
      const fileItem: any = await file.base64ToFile(params)

      images.unshift({
        text: base64,
        file: fileItem,
        lastModified: fileItem.lastModified,
      })

      onChange(images)

      const newList: number[] = []
      checkList.forEach(item => newList.push(item + 1))
      setCheckList(newList)
    } catch (e) {
      setError(e.message)
      setShowError(true)
    }
    setShowEditor(false)
  }

  const addIndex = (index: number) => {
    checkList.push(index)
    setCheckList([...checkList])
  }

  const removeIndex = (index: number) => {
    let i = checkList.indexOf(index)
    if (i >= 0) {
      checkList.splice(i, 1)
      setCheckList([...checkList])
    }
  }

  const onDownload = () => {
    // if (!readOnly) {
    checkList.forEach(async index => {
      let item = images[index]
      let img = await file.urlToBase64({ url: item.FileURL })
      await saveAs(img, item.FileName)
    })
    // } else {
    //   checkList.forEach(index => {
    //     let item = images[index]
    //     saveAs(item.file, item.file.name)
    //   })
    // }
  }

  useEffect(() => {
    props.onChangeRemove?.(removeImgList)
  }, [removeImgList])

  const onRemove = () => {
    if (checkList.length > 0) {
      let newList = []
      let removeList = []
      let index = 0
      for (let item of images) {
        if (checkList.indexOf(index) < 0) {
          newList.push(item)
        } else {
          if (item.FileID != undefined) {
            removeList.push(item.FileID)
          }
        }
        index++
      }
      onChange(newList)
      setCheckList([])
      setRemoveImgList(removeList)
    }
  }

  const textPicture = translate(TKeys.Common.picture)
  const textCancel = translate(TKeys.Common.cancel)

  let title
  if (edit) {
    let removeBtn = readOnly ? undefined : <TrashBtn onClick={onRemove} />
    title = (
      <Col span={24}>
        <EditTitle className="std-font-regular">
          <span onClick={() => setEdit(false)}>{textCancel}</span>
          <label>
            <DownloadDisableBtn onClick={onDownload} />
            {removeBtn}
          </label>
        </EditTitle>
      </Col>
    )
  } else {
    title = (
      <Col span={24}>
        <Title className="std-font-bold">
          <label>{textPicture}</label>
          <span className="std-font-regular" onClick={() => setEdit(true)}>
            {translate(TKeys.Common.pick_picture)}
          </span>
        </Title>
      </Col>
    )
  }

  let count = [3, 7, 11, 15, 19, 23, 27, 31, 35, 39, 43, 47, 51]
  const content = images.map((item, index) => {
    let btn
    let imgDiv
    let img = item.FileURL ? item.FileURL : item.text
    if (edit) {
      const hasChecked = checkList.includes(index)
      let click
      if (hasChecked) {
        click = () => removeIndex(index)
        btn = <Checked src={imgCheck} alt="" onClick={click} />
      } else {
        click = () => addIndex(index)
        btn = <Unchecked onClick={click} />
      }

      imgDiv = <LinkImage src={img} alt="" onClick={click} />
    } else {
      imgDiv = (
        <Image
          src={img}
          alt=""
          onClick={() => props.onThumbnailOpen(index, images, readOnly)}
        />
      )
    }

    return (
      <Col
        span={6}
        style={count.includes(index) ? css.firstCol : css.col}
        key={index}
      >
        {' '}
        {imgDiv} {btn}
      </Col>

      // This Comment Style for don't have picker input on the page
      // <Col span={6} style={css.newCol} key={index}>
      //   {imgDiv}
      //   {btn}
      // </Col>
    )
  })

  return (
    <>
      <Editor
        visible={showEditor}
        image={fileEditor.base64}
        onClose={() => setShowEditor(false)}
        onOk={onAddImage}
      />
      {breadcrumb}
      <Layout.Content className="mid-content">
        <div className="main-content activity-detail">
          <Row style={{ marginBottom: '24px' }}>{title}</Row>

          <Row>
            <Col span={6} style={css.firstCol}>
              <Picker
                disabled={images.length === numberOfImage || readOnly}
                loading={loading}
                height="260"
                width="260"
                text={textPicture}
                accept=".png,.jpg,.heic"
                onChange={addFile}
                isMultiple={isMultiple}
              />
            </Col>
            {content}
          </Row>
        </div>
      </Layout.Content>
    </>
  )
}

const css: any = {
  firstCol: {
    width: '260px',
    marginBottom: '22px',
  },
  col: {
    textAlign: 'right',
    width: '260px',
    marginLeft: '23px',
    marginBottom: '22px',
  },
  newCol: {
    marginBottom: '22px',
  },
}

const Title = styled.div`
  height: 30px;
  label {
    font-size: 28px;
    color: black;
    position: absolute;
    bottom: 0;
  }

  span {
    font-size: 18px;
    color: #003168;
    position: absolute;
    bottom: 0;
    right: 0;
  }

  span:hover {
    cursor: pointer;
  }
`

const EditTitle = styled.div`
  font-size: 18px;
  color: #003168;
  height: 30px;

  span:hover {
    cursor: pointer;
  }

  label {
    float: right;

    img:last-child {
      margin-left: 24px;
    }
  }
`

const CssImg = `
  width: 260px;
  height: 260px;
  object-fit: cover;
  border-radius: 5px;
`
const Image = styled.img`
  ${CssImg}
`

const LinkImage = styled.img`
  ${CssImg} :hover {
    cursor: pointer;
  }
`

const Checked = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  position: absolute;
  z-index: 2;
  right: 8px;
  bottom: 8px;
  :hover {
    cursor: pointer;
  }
`

const Unchecked = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid white;
  position: absolute;
  z-index: 2;
  right: 8px;
  bottom: 8px;
  background-color: rgba(0, 0, 0, 0.3);
  :hover {
    cursor: pointer;
  }
`

export default ImageDisplay
